/**
 * OpenOrganizationState is the slice of the router's state the console's open organization is
 * read from: the location in view, its search parsed, and the matched routes, the deepest of
 * which carries every path param of the routes above it.
 */
export interface OpenOrganizationState {
  readonly location: { readonly pathname: string; readonly search: unknown };
  readonly matches: readonly { readonly params: unknown }[];
}

/**
 * STUDIO_PATH is Map Studio's own path, whose scope rides its search rather than its path, and
 * the root of the studio's other pages: the embed preview names the organization it previews in
 * the same `organization_id`.
 */
const STUDIO_PATH = "/studio";

/**
 * namedId reads an id a record names, or null where it names none: the value is no object, the
 * member is absent, or it is no non-empty string.
 */
const namedId = (value: unknown, read: (record: object) => unknown): string | null => {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const id = read(value);

  return typeof id === "string" && id !== "" ? id : null;
};

/**
 * openOrganizationOf answers which organization the console has open, the one its Data API and
 * Tile API requests name in `X-Mountaya-Organization` so the API records them as that
 * organization's `studio-online` usage.
 *
 * In Map Studio it is the scope the sidenav has open, the `organization_id` of the studio's
 * search, and none in the personal space, which omits it; the embed preview the studio opens
 * names the organization it previews the same way. On an organization's own pages it is that
 * organization, whose maps (the security page's boundary map, the preferences preview) draw for
 * it. Everywhere else it is none. Only the console session sends it: the hosted embed's key and a
 * share link's token are no console session, whatever this answers on their pages. Nothing here is trusted: the API honours the header only
 * while the signed-in user is a member of the organization it names, and it changes nothing a
 * request is answered.
 */
export const openOrganizationOf = ({ location, matches }: OpenOrganizationState): string | null => {
  if (location.pathname === STUDIO_PATH || location.pathname.startsWith(`${STUDIO_PATH}/`)) {
    return namedId(location.search, (search) =>
      "organization_id" in search ? search.organization_id : undefined,
    );
  }

  return namedId(matches.at(-1)?.params, (params) =>
    "organizationId" in params ? params.organizationId : undefined,
  );
};
